// Release day: the old single-slot draft the create screen used before server-saved drafts
// (the since-deleted web/src/components/create/draft.js, key `crm.ticket-draft.v1`). If one
// is still in this browser the agent is asked once whether to Keep it (it becomes a server
// draft) or Discard it, and either way the slot is then deleted. The slot belongs to the
// browser, not to a person, so it may hold the previous agent's call: it is never imported
// silently.
//
// This module never imported `create/draft.js`: that file was deleted when the create screen
// moved onto drafts, and this one had to outlive it. `draftHasContent` below is therefore a
// COPY of that file's function (same rules, same order), kept here for that reason. The copy
// does not need to follow later changes to the original: nothing writes the slot any more.
//
// Every storage call is wrapped: localStorage throws in private windows.

export const LEGACY_KEY = 'crm.ticket-draft.v1';

/**
 * Whether a draft is worth keeping. A form the agent only opened (default form, default
 * queue, nothing typed) is not. Copied from create/draft.js; see the header.
 * `defaultAssigneeId` is the owner a freshly opened form carries; only a CHANGED owner counts.
 */
function draftHasContent({ caller, fields, composer } = {}, { defaultAssigneeId = '' } = {}) {
  if (caller?.customer || caller?.organization) return true;
  if (composer?.subject?.trim() || composer?.body_text?.trim()) return true;
  if (fields?.tags?.length || fields?.follower_user_ids?.length || fields?.cc_customer_ids?.length) return true;
  if (fields?.device_serial?.trim()) return true;
  if (fields?.assigned_user_id && String(fields.assigned_user_id) !== String(defaultAssigneeId)) return true;
  if (fields?.links?.length || fields?.parent || fields?.children?.length) return true;
  if (fields?.custom_fields && Object.values(fields.custom_fields).some(hasValue)) return true;
  return false;
}

function hasValue(value) {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (value === false) return false;
  return true;
}

/**
 * The old slot's draft, or null when there is none, it is unreadable, or it holds nothing
 * worth offering. Does not delete it: that is `clearLegacySlot`, after the agent chooses.
 *
 * No age cut-off (the old loader dropped a slot older than a week): the agent is asked, so a
 * stale one costs a click and a wrongly dropped one costs a call's notes.
 *
 * `defaultAssigneeId` should be the signed-in user's id when known. The old screen never
 * stored a form whose only content was its default owner, so with the default `''` a slot
 * carrying an owner is read as content, which is the safe direction.
 *
 * @returns {{ caller: object, fields: object, composer: object, saved_at: number|null } | null}
 */
export function readLegacySlot(storage, { defaultAssigneeId = '' } = {}) {
  let stored;
  try {
    const raw = storage.getItem(LEGACY_KEY);
    if (!raw) return null;
    stored = JSON.parse(raw);
  } catch {
    return null;
  }
  const draft = stored?.draft;
  if (!draft || typeof draft !== 'object') return null;
  const slice = {
    caller: draft.caller ?? { organization: null, location: null, customer: null },
    fields: draft.fields ?? {},
    composer: draft.composer ?? {},
  };
  if (!draftHasContent(slice, { defaultAssigneeId })) return null;
  return { ...slice, saved_at: typeof stored.saved_at === 'number' ? stored.saved_at : null };
}

export function clearLegacySlot(storage) {
  try {
    storage.removeItem(LEGACY_KEY);
  } catch {
    // The slot stays and the notice may show again; nothing better to do.
  }
}
